import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Loader2, X, XCircle } from "lucide-react";
import { api, extractErrorMessage } from "@/lib/api";
import { DOC_KINDS, uploadPersonnelDoc } from "./PersonnelDocsField";

const OK_EXT = /\.(pdf|jpe?g|png)$/i;
const tcOf = (name) => (name.match(/(?<!\d)\d{11}(?!\d)/) || [])[0] || "";

export const BulkDocsModal = ({ onClose, onDone }) => {
  const [kind, setKind] = useState("ise_giris");
  const [people, setPeople] = useState([]);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/personnel").then((r) => setPeople(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  const byTc = useMemo(() => Object.fromEntries(people.map((p) => [String(p.sicil_no || "").trim(), p])), [people]);
  useEffect(() => { setItems((xs) => xs.map((it) => (it.pid || !it.tc ? it : { ...it, pid: byTc[it.tc]?.id || "" }))); }, [byTc]);
  const dupCount = (pid) => items.filter((x) => x.pid === pid && x.status !== "bad").length;

  const addFiles = (files) => {
    const next = [...files].map((f) => {
      const tc = tcOf(f.name);
      return { file: f, tc, pid: byTc[tc]?.id || "", status: OK_EXT.test(f.name) ? "" : "bad" };
    });
    setItems((x) => [...x, ...next]);
  };
  const setPid = (i, pid) => setItems(items.map((it, j) => (j === i ? { ...it, pid } : it)));
  const ready = items.filter((it) => it.pid && it.status !== "bad" && it.status !== "ok");
  const unmatched = items.filter((it) => !it.pid && it.status !== "bad").length;

  const upload = async () => {
    setBusy(true);
    let ok = 0;
    const out = [...items];
    for (let i = 0; i < out.length; i++) {
      const it = out[i];
      if (!it.pid || it.status === "bad" || it.status === "ok") continue;
      out[i] = { ...it, status: "up" }; setItems([...out]);
      try { await uploadPersonnelDoc(it.pid, kind, it.file); out[i] = { ...it, status: "ok" }; ok++; }
      catch (e) { out[i] = { ...it, status: "err", err: extractErrorMessage(e, "Yüklenemedi") }; }
      setItems([...out]);
    }
    setBusy(false);
    toast.success(`${ok} bildirge yüklendi`);
    if (ok) onDone?.();
  };
  const kindLabel = DOC_KINDS.find((k) => k.k === kind)?.label;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" data-testid="bulk-docs-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="p-3 border-b flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">SGK Bildirgesi Toplu Yükleme</div>
            <div className="text-[11px] text-slate-500">Dosya adındaki 11 haneli TC kimlik no ile personel otomatik eşleşir · mevcut bildirge varsa yenisiyle değiştirilir</div>
          </div>
          <button onClick={onClose} data-testid="bulk-docs-close" className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={14} /></button>
        </div>
        <div className="p-3 flex items-center gap-2 flex-wrap border-b">
          <span className="text-xs font-semibold text-slate-600">Belge türü:</span>
          {DOC_KINDS.map((k) => (
            <button key={k.k} type="button" disabled={busy} onClick={() => setKind(k.k)} data-testid={`bulk-docs-kind-${k.k}`}
              className={`px-2.5 py-1 rounded text-xs font-semibold ${kind === k.k ? "bg-slate-900 text-white" : "border border-slate-300 text-slate-700"}`}>{k.label}</button>
          ))}
          <label className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 bg-teal-700 hover:bg-teal-800 text-white rounded-lg text-xs font-semibold cursor-pointer">
            <FileUp size={13} /> Dosyaları Seç
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid="bulk-docs-input"
              onChange={(e) => { addFiles(e.target.files || []); e.target.value = ""; }} />
          </label>
        </div>
        <div className="p-3 overflow-y-auto flex-1">
          {!items.length ? <div className="text-center text-sm text-slate-400 py-10">Örn. <span className="font-mono">12345678901_giris.pdf</span> gibi adlandırılmış dosyaları seçin</div> : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0"><tr><th className="text-left px-2 py-1.5">Dosya</th><th className="text-left px-2 py-1.5">TC</th><th className="text-left px-2 py-1.5">Personel</th><th className="w-16" /></tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${!it.pid && it.status !== "bad" ? "bg-amber-50/60" : ""}`} data-testid={`bulk-docs-row-${i}`}>
                    <td className="px-2 py-1 truncate max-w-[200px]" title={it.file.name}>{it.file.name}</td>
                    <td className="px-2 py-1 font-mono">{it.tc || <span className="text-amber-700">bulunamadı</span>}</td>
                    <td className="px-2 py-1">
                      {it.status === "bad" ? <span className="text-rose-600">PDF/JPG/PNG değil · atlanacak</span> : (
                        <select value={it.pid} disabled={busy || it.status === "ok"} onChange={(e) => setPid(i, e.target.value)} data-testid={`bulk-docs-person-${i}`}
                          className={`w-56 px-2 py-1 border rounded text-xs ${it.pid ? "border-slate-300" : "border-amber-400"}`}>
                          <option value="">— personel seçin —</option>
                          {people.map((p) => <option key={p.id} value={p.id}>{p.ad_soyad} · {p.sicil_no}{p.aktif === false ? " (pasif)" : ""}</option>)}
                        </select>
                      )}
                      {it.pid && dupCount(it.pid) > 1 && it.status !== "ok" && (
                        <div className="text-[10px] text-rose-600 font-semibold" data-testid={`bulk-docs-dup-${i}`}>Bu kişiye birden fazla dosya eşleşti · son yüklenen geçerli olur</div>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right" data-testid={`bulk-docs-status-${i}`}>
                      {it.status === "up" && <Loader2 size={13} className="inline animate-spin" />}
                      {it.status === "ok" && <CheckCircle2 size={14} className="inline text-emerald-600" />}
                      {it.status === "err" && <span title={it.err}><XCircle size={14} className="inline text-rose-600" /></span>}
                      {!it.status && <button type="button" disabled={busy} onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600"><X size={13} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-3 border-t flex items-center justify-between bg-slate-50 rounded-b-xl">
          <div className="text-xs text-slate-600" data-testid="bulk-docs-summary">{ready.length} dosya hazır · {unmatched} eşleşmedi</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold">Kapat</button>
            <button onClick={upload} disabled={busy || !ready.length} data-testid="bulk-docs-upload"
              className="px-3 py-1.5 bg-teal-700 hover:bg-teal-800 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
              {busy ? <Loader2 size={14} className="inline animate-spin" /> : `${ready.length} dosyayı ${kindLabel} olarak yükle`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
